import { useState } from "react";
import { toast } from "sonner";
import {
  useBulkAssignGroupMutation,
  useBulkRemoveGroupMutation,
  useCreateAndAssignGroupMutation,
  useCustomerGroupsQuery,
} from "../../api/queries/customerGroupsQueries";

export function CustomerGroupBulkModal({
  customerIds,
  mode,
  onClose,
  onDone,
}: {
  customerIds: number[];
  mode: "assign" | "remove";
  onClose: () => void;
  onDone: () => void;
}) {
  const { data: groups = [] } = useCustomerGroupsQuery();
  const [groupId, setGroupId] = useState<number | "">("");
  const [newGroupName, setNewGroupName] = useState("");
  const [createNew, setCreateNew] = useState(false);
  const assignMutation = useBulkAssignGroupMutation();
  const removeMutation = useBulkRemoveGroupMutation();
  const createAssignMutation = useCreateAndAssignGroupMutation();

  const handleSubmit = async () => {
    try {
      if (mode === "remove") {
        if (groupId === "") return;
        await removeMutation.mutateAsync({
          group_id: Number(groupId),
          customer_organization_ids: customerIds,
        });
      } else if (createNew && newGroupName.trim()) {
        await createAssignMutation.mutateAsync({
          name: newGroupName.trim(),
          customer_organization_ids: customerIds,
        });
      } else {
        if (groupId === "") return;
        await assignMutation.mutateAsync({
          group_id: Number(groupId),
          customer_organization_ids: customerIds,
        });
      }
      onDone();
      onClose();
    } catch {
      toast.error("Failed to update customer groups");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-800">
          {mode === "assign" ? "Assign group" : "Remove from group"}
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          {customerIds.length} customer(s) selected
        </p>
        <div className="mt-4 space-y-3">
          {mode === "assign" && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={createNew}
                onChange={(e) => setCreateNew(e.target.checked)}
              />
              Create new group
            </label>
          )}
          {createNew && mode === "assign" ? (
            <input
              type="text"
              placeholder="Group name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          ) : (
            <select
              value={groupId}
              onChange={(e) =>
                setGroupId(e.target.value ? Number(e.target.value) : "")
              }
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Select group</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
